/* eslint-disable @typescript-eslint/no-deprecated -- ok to reference the deprecated methods that come from this same file. We will be removing them eventually */
import moment, { Moment } from '../../general-foreman/node_modules/moment'; //Not ideal, but we only need to reference this here to get the typedefs, we aren't actually importing any code
import { DATE_FORMAT_FRIENDLY, DATE_FORMAT_ODATA, DATE_FORMAT_SHORT_WEEKDAY_NAME } from '../constants/date-format-constants';
import { NON_DIGITS_PATTERN } from '../constants/pattern-constants';
import { IDayOfWeek } from '../interfaces/dates';

/**
 * @description When a `workScheduleRule` and a `dayOfWeek` are passed in, this will return true if the day falls within that schedule rule.
 * This currently ONLY SUPPORTS the most common workScheduleRules of `"NORM"`, `"10MT"`, and `"10TF"`.
 * Any other schedule rule will return `true` here since it's not a common one we know how to process here and we should assume it might be valid
 * This only takes a day of a work schedule into account, not the hours!
 * @example isDayWithinScheduleRule("NORM", "tue") //true
 * @example isDayWithinScheduleRule("NORM", "sat") //false
 * @example isDayWithinScheduleRule("10MT", "thu") //true
 * @example isDayWithinScheduleRule("10MT", "fri") //false
 * */
export function isDayWithinScheduleRule(workScheduleRule: string, dayOfWeek: IDayOfWeek): boolean {
  if (workScheduleRule === 'NORM') {
    //NORM schedule is Monday - Friday
    return /^(?:mon|tue|wed|thu|fri)$/.test(dayOfWeek);
  } else if (workScheduleRule === '10MT') {
    //10MT schedule is Monday - Thursday
    return /^(?:mon|tue|wed|thu)$/.test(dayOfWeek);
  } else if (workScheduleRule === '10TF') {
    //10TF schedule is Tuesday - Friday
    return /^(?:tue|wed|thu|fri)$/.test(dayOfWeek);
  } else {
    //Any other schedule type we don't process here
    //we should assume they are valid for a given day so we do not accidentally restrict an action
    return true;
  }
}

/**
 * @description A simple hook into the Moment library to return a date as a string formatted for OData
 * @example `YYYY-MM-DDTHH:mm:ss`
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getOdataString(momentObj: Moment): string {
  return momentObj.format(DATE_FORMAT_ODATA);
}

/**
 * @description A simple hook into the Moment library to return a date as a string formatted for OData, ready to be used in a query string
 * @example `datetime'YYYY-MM-DDTHH:mm:ss'`
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getFilterString(momentObj: Moment): string {
  return `datetime'${getOdataString(momentObj)}'`;
}

/**
 * @description A simple hook into the Moment library to return a date as a string formatted into a friendly & human-readable date string
 * @example `MM/DD/YYYY`
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getHumanReadableDateString(momentObj: Moment): string {
  return momentObj.format(DATE_FORMAT_FRIENDLY);
}

/**
 * @description A simple hook into the Moment library to return a date as a string formatted as a Unix datetime string
 * @example `/Date(1360002924000)/`
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getUnixString(momentObj: Moment): string {
  return `/Date(${momentObj.valueOf().toString()})/`;
}

/**
 * @description Converts an SAP date string to a UTC date as a Moment object
 * @example convertSapDateToMoment("/Date(1360002924000)/")
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function convertSapDateToMoment(sapDate: string): Moment {
  const string = sapDate.replace(NON_DIGITS_PATTERN, '');
  const number = Number(string);
  const date = new Date(number);
  return moment(date).utc();
}

/**
 * @description Converts an SAP date string into a friendly & human-readable date string
 * @example `MM/DD/YYYY`
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function convertSapDateToHumanReadable(sapDate: string): string {
  return getHumanReadableDateString(convertSapDateToMoment(sapDate));
}

/**
 * @description Converts an SAP date string into whatever Moment date format is provided
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function convertSapDateToFormat(sapDate: string, format: string): string {
  return convertSapDateToMoment(sapDate).format(format);
}

/**
 * @description Converts an SAP date string into just the short day of the week name, returned as lowercase
 * @example "mon", "tue", "wed", etc.
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function convertSapDateToWeekDayString(sapDate: string): IDayOfWeek {
  return convertSapDateToFormat(sapDate, DATE_FORMAT_SHORT_WEEKDAY_NAME).toLowerCase() as IDayOfWeek;
}

/**
 * @description Converts a Moment object into just the short day of the week name, returned as lowercase
 * @example "mon", "tue", "wed", etc.
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getDayString(momentObj: Moment): IDayOfWeek {
  return momentObj.format(DATE_FORMAT_SHORT_WEEKDAY_NAME).toLowerCase() as IDayOfWeek;
}

/**
 * @description Returns the first day of the week relative the date that was passed in
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function getWeekStartingDate(momentObj: Moment): Moment {
  return momentObj.day(0);
}

/**
 * @description Compares 2 date objects, returns a boolean if they are the same day or not
 * @deprecated **❌DO NOT USE THIS❌**, it is based on Moment and will dramatically increase the bundle size!
 */
export function isSameDay(date1: Moment, date2: Moment): boolean {
  return moment(date1).isSame(date2, 'day');
}
